import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, StatTile, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import { MoneyInput } from "../components/MoneyInput";
import PaymentModal from "../components/PaymentModal";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../components/ConfirmDialog";
import { useMultiSelect } from "../lib/multiSelect";
import MultiSelectBar from "../components/MultiSelectBar";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle, ArrowCounterClockwise, CaretRight } from "@phosphor-icons/react";

const TYPE_TONE = { receivable: "yellow", payable: "red", received: "green", paid: "zinc" };
const TYPE_LABEL = { receivable: "to receive", payable: "to pay", received: "received", paid: "paid" };

const TABS = [
  { key: "to_receive", label: "To Receive", tone: "text-green-700", bg: "bg-green-50 border-green-500", match: (e) => e.entry_type === "receivable" && e.status !== "cleared" },
  { key: "to_pay",     label: "To Pay",     tone: "text-red-700",   bg: "bg-red-50 border-red-500",   match: (e) => e.entry_type === "payable"    && e.status !== "cleared" },
  { key: "received",   label: "Received",   tone: "text-emerald-800", bg: "bg-emerald-50 border-emerald-500", match: (e) => (e.entry_type === "received") || (e.entry_type === "receivable" && e.status === "cleared") },
  { key: "paid",       label: "Paid",       tone: "text-zinc-800",  bg: "bg-zinc-50 border-zinc-400",  match: (e) => (e.entry_type === "paid")    || (e.entry_type === "payable"    && e.status === "cleared") },
];

export default function Accounting() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { confirm } = useConfirm();
  const ms = useMultiSelect();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("to_receive");
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const [payingEntry, setPayingEntry] = useState(null);   // pending recv/pay row → open PaymentModal

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/accounting"), api.get("/accounting/summary")]);
    setEntries(a.data); setSummary(b.data);
  };
  useEffect(() => { load(); }, []);

  const del = async (e) => {
    if (e.status === "cleared" && !isAdmin) { toast.error("Only admin can delete cleared entries"); return; }
    const ok = await confirm({
      title: "Delete accounting entry?",
      message: `${e.entry_type} · ${e.party} · ₹${e.amount.toLocaleString("en-IN")}\n\nThis cannot be undone.`,
      tone: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/accounting/${e.id}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const markCleared = async (e) => {
    await api.put(`/accounting/${e.id}`, { ...e, status: "cleared" });
    load();
  };

  const revoke = async (e) => {
    const ok = await confirm({
      title: "Revoke cleared entry?",
      message: `Move ₹${e.amount.toLocaleString("en-IN")} for ${e.party} back to pending.`,
      tone: "warning",
      confirmLabel: "Revoke",
    });
    if (!ok) return;
    try { await api.post(`/accounting/${e.id}/revoke`); toast.success("Revoked"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const tabForEntry = (e) => {
    if (e.entry_type === "receivable") return e.status === "pending" ? "to_receive" : "received";
    if (e.entry_type === "payable")    return e.status === "pending" ? "to_pay"     : "paid";
    if (e.entry_type === "received")   return "received";
    if (e.entry_type === "paid")       return "paid";
    return "to_receive";
  };

  const tabForGroup = (legs) => {
    const hasPendingRecv = legs.some(e => e.entry_type === "receivable" && e.status === "pending");
    if (hasPendingRecv) return "to_receive";
    const hasPendingPay = legs.some(e => e.entry_type === "payable" && e.status === "pending");
    if (hasPendingPay) return "to_pay";
    const anyReceivableFamily = legs.some(e => ["receivable", "received"].includes(e.entry_type));
    if (anyReceivableFamily) return "received";
    return "paid";
  };

  const grouped = useMemo(() => {
    const byDoc = new Map();
    const singles = [];
    for (const e of entries) {
      if (!e.linked_doc_no) { singles.push(e); continue; }
      const k = e.linked_doc_no;
      if (!byDoc.has(k)) byDoc.set(k, []);
      byDoc.get(k).push(e);
    }

    const rows = [];
    byDoc.forEach((legs, doc) => {
      if (legs.length === 1) {
        rows.push({ tab: tabForEntry(legs[0]), party: legs[0].party, amount: legs[0].amount || 0, entry: legs[0] });
        return;
      }
      const targetTab = tabForGroup(legs);
      // Outstanding = sum of pending receivable/payable amounts MINUS the
      // partial payments already applied. `balance_due` is enriched by the
      // backend so this is a single subtraction.
      const outstanding = legs
        .filter(e => e.status === "pending" && ["receivable", "payable"].includes(e.entry_type))
        .reduce((s, e) => s + Number(e.balance_due != null ? e.balance_due : (e.amount || 0)), 0);
      // Historical "cleared" tabs (received/paid) still show the sum of legs
      // for that tab, but we never add advance receipts on top of a pending
      // receivable — that would double-count the invoice. Use ONLY the
      // pending leg for `to_receive` / `to_pay` and ONLY the cleared leg for
      // `received` / `paid`.
      const clearedTotal = legs
        .filter(e => e.status === "cleared" && ["received", "paid"].includes(e.entry_type))
        .reduce((s, e) => s + (e.amount || 0), 0);
      rows.push({
        tab: targetTab,
        party: legs[0].party,
        amount: (targetTab === "to_receive" || targetTab === "to_pay") ? outstanding : clearedTotal,
        aggregated: true,
        group: buildAggregatedGroup(doc, legs),
      });
    });
    singles.forEach(e => rows.push({ tab: tabForEntry(e), party: e.party, amount: e.amount || 0, entry: e }));

    let filtered = rows.filter(r => r.tab === tab);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter(r => {
        const party = (r.party || "").toLowerCase();
        const notes = r.entry ? (r.entry.notes || "").toLowerCase() : (r.group.all.map(e => e.notes || "").join(" ").toLowerCase());
        const doc = r.aggregated ? (r.group.doc || "").toLowerCase() : "";
        return party.includes(q) || notes.includes(q) || doc.includes(q);
      });
    }

    const map = new Map();
    for (const r of filtered) {
      const key = (r.party || "Unnamed").trim();
      if (!map.has(key)) map.set(key, { party: key, rows: [], total: 0, oldest: null });
      const g = map.get(key);
      g.rows.push(r);
      g.total += r.amount;
      const date = r.aggregated ? (r.group.advance?.date || r.group.balance?.date || "") : (r.entry?.date || "");
      if (date && (!g.oldest || date < g.oldest)) g.oldest = date;
    }
    const groups = [...map.values()].sort((a, b) => b.total - a.total);
    return { groups, total: groups.reduce((s, g) => s + g.total, 0) };
  }, [entries, tab, search]);

  const tabCounts = useMemo(() => {
    const counts = { to_receive: 0, to_pay: 0, received: 0, paid: 0 };
    const byDoc = new Map();
    const singles = [];
    for (const e of entries) {
      if (!e.linked_doc_no) { singles.push(e); continue; }
      const k = e.linked_doc_no;
      if (!byDoc.has(k)) byDoc.set(k, []);
      byDoc.get(k).push(e);
    }
    byDoc.forEach(legs => {
      if (legs.length === 1) counts[tabForEntry(legs[0])] += 1;
      else counts[tabForGroup(legs)] += 1;
    });
    singles.forEach(e => counts[tabForEntry(e)] += 1);
    return counts;
  }, [entries]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-zinc-900 tracking-tight">Accounting & Cash</h1>
          <p className="text-zinc-500 text-sm">Aggregated by party. Expand any row to see individual entries.</p>
        </div>
        <Btn onClick={() => setOpen(true)} className="shadow-lg shadow-orange-500/20 active:scale-95 transition-transform">
          <Plus size={16} weight="bold" /> New Entry
        </Btn>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="To Receive" val={summary.receivable_pending} color="text-green-600" />
          <StatCard label="To Pay" val={summary.payable_pending} color="text-red-600" />
          <StatCard label="Received" val={summary.total_received} color="text-emerald-600" />
          <StatCard label="Paid" val={summary.total_paid} color="text-zinc-800" />
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center bg-zinc-100/50 p-1 rounded-xl">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-5 py-2.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${tab === t.key ? "bg-white text-zinc-900 shadow-sm ring-1 ring-black/5" : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200/50"}`}>
            {t.label} <span className="ml-1.5 opacity-50 bg-black/5 px-1.5 py-0.5 rounded-full">{tabCounts[t.key]}</span>
          </button>
        ))}
        <div className="ml-auto">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="bg-white border border-zinc-200 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-orange-500/20 outline-none w-48" />
        </div>
      </div>

      <Card className="!p-0 overflow-hidden rounded-2xl shadow-sm border-zinc-100">
        {grouped.groups.length === 0 ? (
          <div className="p-20 text-center text-zinc-400">No entries in current view.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {grouped.groups.map(g => (
              <div key={g.party}>
                <button onClick={() => setExpanded(x => ({ ...x, [g.party]: !x[g.party] }))} className="w-full flex items-center justify-between p-5 hover:bg-zinc-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`transition-transform ${expanded[g.party] ? "rotate-90 text-orange-600" : "text-zinc-400"}`}><CaretRight size={16} weight="bold" /></div>
                    <div className="text-left"><div className="font-bold text-sm text-zinc-900">{g.party}</div><div className="text-[11px] text-zinc-500">{g.rows.length} entries {g.oldest && `· ${g.oldest}`}</div></div>
                  </div>
                  <div className="font-mono font-black text-sm">₹{g.total.toLocaleString("en-IN")}</div>
                </button>
                {expanded[g.party] && (
                  <div className="bg-zinc-50/50 border-t border-zinc-100 px-4 pb-4">
                    <table className="w-full text-left">
                      <thead className="text-[10px] uppercase text-zinc-400 border-b border-zinc-200/50">
                        <tr><th className="py-3">Date</th><th>Amount</th><th>Status</th><th>Notes</th><th className="text-right">Actions</th></tr>
                      </thead>
                      <tbody>
                        {g.rows.map(row => row.aggregated ? (
                          <AggregatedRow key={`agg-${row.group.doc}`} row={row.group} isAdmin={isAdmin} ms={ms} onClear={markCleared} onRevoke={revoke} onDelete={del} onPay={setPayingEntry} />
                        ) : (
                          <SingleRow key={row.entry.id} e={row.entry} isAdmin={isAdmin} ms={ms} onClear={markCleared} onRevoke={revoke} onDelete={del} onPay={setPayingEntry} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {open && <EntryModal onClose={() => setOpen(false)} onAdded={() => { setOpen(false); load(); }} />}
      {payingEntry && <PaymentModal entry={payingEntry} onClose={() => setPayingEntry(null)}
        onSaved={() => { setPayingEntry(null); load(); }} />}
      <MultiSelectBar count={ms.selected.length} ids={ms.selected} resource="accounting" label="entries" onCleared={() => { ms.clear(); load(); }} />
    </div>
  );
}

function StatCard({ label, val, color }) {
  return <div className="bg-white p-5 rounded-2xl border border-zinc-100 shadow-sm"><div className="text-[10px] uppercase font-bold text-zinc-400 tracking-widest mb-1">{label}</div><div className={`text-xl font-black font-mono ${color}`}>₹{val.toLocaleString("en-IN")}</div></div>;
}

function EntryModal({ onClose, onAdded }) {
  const { confirm } = useConfirm();
  const [sources, setSources] = useState([]);
  const [f, setF] = useState({
    entry_type: "receivable", amount: "", party: "", notes: "",
    date: new Date().toISOString().slice(0, 10), status: "pending",
    source: "", due_date: "",
  });
  const upd = (k, v) => setF({ ...f, [k]: v });

  React.useEffect(() => {
    api.get("/settings").then(r => setSources(r.data.payment_sources || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      // master-first prompt for party
      if (f.party.trim()) {
        const kind = (f.entry_type === "receivable" || f.entry_type === "received") ? "customer" : "supplier";
        const r = await api.get("/parties", { params: { kind } });
        const found = r.data.some(p => p.name.trim().toLowerCase() === f.party.trim().toLowerCase());
        if (!found) {
          const yes = await confirm({
            title: `Add "${f.party}" to ${kind}s?`,
            message: `They aren't in your ${kind} master yet.\nAdding them keeps outstanding tracking accurate.`,
            tone: "info",
            confirmLabel: "Add & Continue",
            cancelLabel: "Skip",
          });
          if (yes) {
            try {
              await api.post("/parties", { kind, name: f.party, credit_days: kind === "customer" ? 15 : undefined });
              toast.success(`${kind === "customer" ? "Customer" : "Supplier"} added to master`);
            } catch (partyErr) {
              console.warn("Failed to add party to master:", partyErr?.response?.data?.detail || partyErr?.message);
            }
          }
        }
      }
      await api.post("/accounting", { ...f, amount: parseFloat(f.amount) });
      toast.success("Entry added");
      onAdded();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal open onClose={onClose} title="New Accounting Entry">
      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="entry-form">
        <Field label="Type">
          <Select value={f.entry_type} onChange={e => upd("entry_type", e.target.value)} data-testid="entry-type">
            <option value="receivable">Receivable (will receive)</option>
            <option value="payable">Payable (will pay)</option>
            <option value="received">Received</option>
            <option value="paid">Paid</option>
          </Select>
        </Field>
        <Field label="Status">
          <Select value={f.status} onChange={e => upd("status", e.target.value)}>
            <option value="pending">Pending</option><option value="cleared">Cleared</option>
          </Select>
        </Field>
        <Field label="Party" full><Input value={f.party} onChange={e => upd("party", e.target.value)} required data-testid="entry-party" placeholder="Customer / Supplier name" /></Field>
        <Field label="Amount"><MoneyInput value={f.amount} onChange={e => upd("amount", e.target.value)} min={0} required placeholder="₹0.00" className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono" data-testid="entry-amount" /></Field>
        <Field label="Date"><DatePicker value={f.date} onChange={e => upd("date", e.target.value)} /></Field>
        <Field label="Due date (optional)">
          <DatePicker value={f.due_date} onChange={e => upd("due_date", e.target.value)} data-testid="entry-due" />
        </Field>
        <Field label="Payment source" full>
          <Select value={f.source} onChange={e => upd("source", e.target.value)} data-testid="entry-source">
            <option value="">— select bank / cash source —</option>
            {sources.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Notes" full><Input value={f.notes} onChange={e => upd("notes", e.target.value)} /></Field>
        <div className="col-span-2 flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" data-testid="entry-submit">Add Entry</Btn>
        </div>
      </form>
    </Modal>
  );
}


// -----------------------------------------------------------------------------
// buildAggregatedGroup — collapse an invoice's advance + balance legs into a
// single row descriptor used by <AggregatedRow>. Called once per linked_doc_no
// pre-tab-filter (see grouped useMemo above).
// -----------------------------------------------------------------------------
function buildAggregatedGroup(doc, legs) {
  const advance = legs.find(
    e => ["received", "paid"].includes(e.entry_type)
      || (["receivable", "payable"].includes(e.entry_type)
          && e.status === "cleared"
          && (e.notes || "").toLowerCase().includes("advance"))
  );
  const balance = legs.find(
    e => ["receivable", "payable"].includes(e.entry_type) && e !== advance
  );
  const orderedLegs = [advance, balance].filter(Boolean);
  legs.forEach(e => { if (!orderedLegs.includes(e)) orderedLegs.push(e); });
  return {
    doc,
    advance: orderedLegs[0],
    balance: orderedLegs[1] || null,
    others: orderedLegs.slice(2),
    total: legs.reduce((s, e) => s + (e.amount || 0), 0),
    all: legs,
  };
}

function SingleRow({ e, isAdmin, ms, onClear, onRevoke, onDelete, onPay }) {
  // For pending receivables/payables, expose the running paid/balance so the
  // operator can see how much is still outstanding without opening a drawer.
  const isPending = ["receivable", "payable"].includes(e.entry_type) && e.status !== "cleared";
  const total = Number(e.amount) || 0;
  const paid = Number(e.paid_total || 0);
  const balance = e.balance_due != null ? Number(e.balance_due) : Math.max(0, total - paid);
  return (
    <tr data-testid={`acc-row-${e.id}`}>
      <td className="font-mono text-xs">
        {isAdmin && (
          <input type="checkbox" checked={ms.isSelected(e.id)} onChange={() => ms.toggle(e.id)}
                 className="accent-orange-600 mr-2" data-testid={`acc-check-${e.id}`} />
        )}
        {e.date}{e.due_date ? <div className="text-[9px] text-orange-700">due {e.due_date}</div> : null}
      </td>
      <td className="font-mono font-bold">
        ₹{e.amount.toLocaleString("en-IN")}
        <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">{TYPE_LABEL[e.entry_type] || e.entry_type}</div>
        {isPending && paid > 0 && (
          <div className="text-[10px] font-mono text-zinc-600 mt-1"
               data-testid={`acc-balance-${e.id}`}>
            <div>Paid ₹{paid.toLocaleString("en-IN")}</div>
            <div className="text-orange-700">Balance ₹{balance.toLocaleString("en-IN")}</div>
          </div>
        )}
      </td>
      <td><span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${e.status === "cleared" ? "bg-emerald-50 border-emerald-500 text-emerald-800" : paid > 0 ? "bg-orange-50 border-orange-500 text-orange-800" : "bg-yellow-50 border-yellow-500 text-yellow-800"}`}>{e.status === "cleared" ? "cleared" : paid > 0 ? "partial" : e.status}</span></td>
      <td className="text-[11px] font-mono text-zinc-600">{e.source || e.linked_doc_no || "—"}</td>
      <td className="text-xs text-zinc-600">{e.notes}</td>
      <td>
        <div className="flex justify-end gap-2">
          {isPending && (
            <button onClick={() => onPay(e)} data-testid={`pay-${e.id}`}
                    className="text-orange-700 hover:text-orange-900 text-[10px] font-bold uppercase tracking-widest"
                    title="Record an amount received / paid against this entry">
              ₹ {e.entry_type === "receivable" ? "Received" : "Paid"}
            </button>
          )}
          {e.status === "cleared" && isAdmin && (
            <button onClick={() => onRevoke(e)} data-testid={`revoke-${e.id}`} className="text-orange-600 hover:text-orange-800" title="Revoke (admin only)"><ArrowCounterClockwiseIco /></button>
          )}
          <button onClick={() => onDelete(e)} className="text-red-600" title="Delete"><TrashIco /></button>
        </div>
      </td>
    </tr>
  );
}

function AggregatedRow({ row, isAdmin, ms, onClear, onRevoke, onDelete, onPay }) {
  const { advance, balance, others, doc, total, all } = row;
  const anyPending = all.some(e => e.status === "pending");
  const balancePaid = Number(balance?.paid_total || 0);
  const balanceDue = balance?.balance_due != null
    ? Number(balance.balance_due)
    : Math.max(0, Number(balance?.amount || 0) - balancePaid);
  const summaryTone = anyPending
    ? (balancePaid > 0 ? "bg-orange-50 border-orange-500 text-orange-800"
                       : "bg-yellow-50 border-yellow-500 text-yellow-800")
    : "bg-emerald-50 border-emerald-500 text-emerald-800";
  return (
    <tr data-testid={`acc-agg-${doc}`} className="align-top">
      <td className="font-mono text-xs">
        {balance && (
          <div>
            <div>{balance.date}{balance.due_date ? <div className="text-[9px] text-orange-700">due {balance.due_date}</div> : null}</div>
          </div>
        )}
        {others?.map(e => (
          <div key={e.id} className="mt-1"><span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">{e.entry_type}</span><div>{e.date}</div></div>
        ))}
      </td>
      <td className="font-mono font-bold">
        {balance && <div className="text-zinc-900">₹{balance.amount.toLocaleString("en-IN")} <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 ml-1">{balance.entry_type === "receivable" ? "receivable" : balance.entry_type === "payable" ? "payable" : balance.entry_type}</span></div>}
        {balance && balancePaid > 0 && (
          <div className="text-[10px] font-mono mt-0.5" data-testid={`acc-agg-balance-${doc}`}>
            <div className="text-zinc-500">Paid ₹{balancePaid.toLocaleString("en-IN")}</div>
            <div className="text-orange-700">Balance ₹{balanceDue.toLocaleString("en-IN")}</div>
          </div>
        )}
        {others?.map(e => (
          <div key={e.id} className="text-zinc-700">₹{e.amount.toLocaleString("en-IN")} <span className="text-[9px] uppercase tracking-widest text-zinc-500 ml-1">{e.entry_type}</span></div>
        ))}
      </td>
      <td>
        <span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${summaryTone}`}>{anyPending ? "partial" : "fully cleared"}</span>
        <div className="text-[10px] font-mono text-zinc-500 mt-1">
          {advance ? "✓ advance" : ""}
          {balance ? (balance.status === "cleared" ? " · ✓ balance" : " · ⏳ balance pending") : ""}
        </div>
      </td>
      <td className="text-[11px] font-mono text-zinc-700">{doc}</td>
      <td className="text-xs text-zinc-600">
        {advance?.notes && <div className="text-emerald-700">{advance.notes}</div>}
        {balance?.notes && <div>{balance.notes}</div>}
      </td>
      <td>
        <div className="flex flex-col items-end gap-1">
          {balance && balance.status === "pending" && (
            <button onClick={() => onPay(balance)} data-testid={`pay-${balance.id}`}
                    className="text-[10px] font-bold uppercase tracking-widest text-orange-700 hover:text-orange-900">
              ₹ {balance.entry_type === "receivable" ? "Received" : "Paid"}
            </button>
          )}
          {isAdmin && (
            <button onClick={() => onDelete(all[0])} className="text-[10px] text-red-600 hover:text-red-800" title="Delete oldest leg">
              <TrashIco />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// tiny icon aliases so the aggregation helpers stay concise
function CheckCircleIco() { return <CheckCircle size={16} weight="fill" />; }
function ArrowCounterClockwiseIco() { return <ArrowCounterClockwise size={14} weight="bold" />; }
function TrashIco() { return <Trash size={14} weight="bold" />; }


